import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FiImage,
  FiPlus,
  FiMessageSquare,
  FiTrash2,
  FiSend,
  FiMenu,
  FiX,
  FiAlertCircle,
  FiUsers,
  FiUser,
  FiEye,
  FiSearch,
  FiPaperclip,
  FiDownload,
  FiFilm,
  FiLock,
  FiRefreshCw,
  FiEdit2,
  FiCornerDownLeft,
  FiExternalLink,
  FiLink,
  FiSettings,
  FiSquare,
  FiCopy,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiPlay,
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-toastify';
import { mediaAIService } from '../services/apiService';
import { useT } from '../utils/i18n';
import { photoAge } from '../utils/photoAge';
import { useAuthStore } from '../utils/store';
import { hasFeature } from '../utils/features';
import ImageEditor, { CHECKERBOARD_STYLE } from '../components/MediaAI/ImageEditor';
import LoaderGames from '../components/MediaAI/LoaderGames';
import { outputGridClass, groupBySection } from './MediaAI.layout';
import { useCustomPrompts, PromptManager, PromptPicker } from '../components/Common/CustomPrompts';

const ADMIN_HISTORY_ROLES = ['admin', 'superadmin', 'manager'];
const MAX_FILES = 10;
const MAX_FILE_BYTES = 200 * 1024 * 1024;
const POLL_INTERVAL_MS = 3000;

const PROVIDER_NAMES = { openai: 'OpenAI', freepik: 'Freepik' };
const IMAGE_PROVIDER_STORAGE_KEY = 'mediaAI.imageProvider';
const VIDEO_MODEL_STORAGE_KEY = 'mediaAI.videoModel';
const ASPECT_STORAGE_KEY = 'mediaAI.aspectRatio';
const GAME_VISIBLE_KEY = 'mediaAI.games.visible';
const ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:5', '4:3'];
const DEFAULT_ASPECT = '16:9';

const readStored = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeStored = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* private mode — not remembered */ }
};

// Small round action button laid over a picture on hover.
const OverlayButton = ({ title, onClick, href, children }) => {
  const className = 'flex items-center justify-center w-8 h-8 rounded-lg bg-black/60 text-white hover:bg-black/80';
  return href ? (
    <a href={href} title={title} className={className}>{children}</a>
  ) : (
    <button type="button" title={title} onClick={onClick} className={className}>{children}</button>
  );
};

const isAllowedFile = (file) => /^image\/(jpeg|png|webp|gif)$/.test(file.type) || file.type.startsWith('video/');

// ─── Attachment shown inside a sent user message ─────────────────────────────
const SentAttachment = ({ attachment, onOpen, onEdit }) => {
  const t = useT();
  const url = mediaAIService.fileUrl(attachment.filename);
  if (attachment.kind === 'video') {
    return (
      <video
        src={url}
        controls
        preload="metadata"
        className="w-56 max-w-full rounded-lg bg-black"
        title={attachment.originalName}
      />
    );
  }
  return (
    <div className="group relative">
      <button onClick={() => onOpen(url)} className="block" title={attachment.originalName}>
        <img
          src={url}
          alt={attachment.originalName}
          loading="lazy"
          className="h-28 w-28 object-cover rounded-lg"
          style={attachment.mimeType === 'image/png' ? CHECKERBOARD_STYLE : undefined}
        />
      </button>
      {onEdit && (
        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <OverlayButton title={t('mediaAI.edit')} onClick={() => onEdit({ filename: attachment.filename }, url)}>
            <FiEdit2 size={14} />
          </OverlayButton>
        </div>
      )}
    </div>
  );
};

// ─── One picture or video in a reply ─────────────────────────────────────────
// compact: a uniform tile in a topic group (same-size cells, cropped to fit),
// instead of the picture at its own shape.
const OutputMedia = ({ output, onOpen, onEdit, onUseImage, compact = false }) => {
  const t = useT();
  const url = mediaAIService.fileUrl(output.filename);
  if (output.kind === 'video') {
    return (
      <div className="w-fit max-w-full">
        <video
          src={url}
          poster={output.poster ? mediaAIService.fileUrl(output.poster) : undefined}
          controls
          preload="metadata"
          className={`max-w-full rounded-xl border border-gray-200 bg-black ${output.height > output.width ? 'max-h-[520px]' : 'max-h-[420px]'}`}
        />
        <a
          href={mediaAIService.fileUrl(output.filename, { download: true })}
          className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800"
        >
          <FiDownload size={13} />
          {t('mediaAI.downloadVideo')}{output.durationSec ? ` · ${output.durationSec}s` : ''}
        </a>
      </div>
    );
  }
  return (
    <div className={compact ? 'min-w-0' : 'w-fit max-w-full'}>
      <div className={`group relative ${compact ? 'w-full' : 'w-fit max-w-full'}`}>
        <button onClick={() => onOpen(url)} className="block w-full">
          <img
            src={url}
            alt={output.source?.title || t('mediaAI.generatedAlt')}
            loading="lazy"
            width={output.width}
            height={output.height}
            className={compact
              ? 'w-full aspect-[4/3] object-cover rounded-xl border border-gray-200 bg-gray-100'
              : `${output.source ? 'max-h-[300px]' : 'max-h-[420px]'} w-auto h-auto max-w-full rounded-xl border border-gray-200`}
            style={output.mimeType === 'image/png' ? CHECKERBOARD_STYLE : undefined}
          />
        </button>
        <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          {onEdit && (
            <OverlayButton title={t('mediaAI.edit')} onClick={() => onEdit(output, url)}>
              <FiEdit2 size={14} />
            </OverlayButton>
          )}
          {onUseImage && (
            <OverlayButton title={t('mediaAI.useInChat')} onClick={() => onUseImage(output, url)}>
              <FiCornerDownLeft size={14} />
            </OverlayButton>
          )}
          <OverlayButton title={t('mediaAI.download')} href={mediaAIService.fileUrl(output.filename, { download: true })}>
            <FiDownload size={15} />
          </OverlayButton>
        </div>
      </div>
      {output.source && (
        <a
          href={output.source.pageUrl || output.source.url}
          target="_blank"
          rel="noopener noreferrer"
          title={output.source.title}
          className="mt-1 flex items-center gap-1 max-w-full text-[11px] text-gray-500 hover:text-gray-800"
        >
          <FiExternalLink size={11} className="flex-shrink-0" />
          <span className="truncate">
            {output.source.site || t('mediaAI.viewSource')}
            {output.source.publishedAt ? ` · ${photoAge(output.source.publishedAt)}` : ''}
            {output.source.license ? ` · ${output.source.license}` : ''}
          </span>
        </a>
      )}
    </div>
  );
};

const MARKDOWN_COMPONENTS = {
  a: (props) => <a className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
};

// Small text button under a message (Copy, Edit), shown on hover like ChatGPT's.
const MessageAction = ({ title, onClick, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    className="flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
  >
    {children}
  </button>
);

const useCopy = () => {
  const [copied, setCopied] = useState(false);
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked — nothing to confirm
    }
  };
  return [copied, copy];
};

// ‹ 2 / 3 › under an edited message: which version is shown, and switch.
const VersionSwitcher = ({ versions, onSwitch, disabled }) => {
  const t = useT();
  const index = versions.current - 1;
  return (
    <div className="flex items-center gap-0.5 text-xs text-gray-500 tabular-nums">
      <button
        type="button"
        title={t('mediaAI.previousVersion')}
        aria-label={t('mediaAI.previousVersion')}
        disabled={disabled || index <= 0}
        onClick={() => onSwitch(versions.group, versions.orders[index - 1])}
        className="flex items-center justify-center w-6 h-6 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <FiChevronLeft size={14} />
      </button>
      <span>{versions.current} / {versions.total}</span>
      <button
        type="button"
        title={t('mediaAI.nextVersion')}
        aria-label={t('mediaAI.nextVersion')}
        disabled={disabled || index >= versions.total - 1}
        onClick={() => onSwitch(versions.group, versions.orders[index + 1])}
        className="flex items-center justify-center w-6 h-6 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <FiChevronRight size={14} />
      </button>
    </div>
  );
};

// ─── One message ─────────────────────────────────────────────────────────────
// onEditMessage(text): resend this (user) message with new text — the
// earlier version is kept. onSwitchVersion(group, order): show another version.
const MessageBubble = ({
  message, onOpen, onRetry, onEdit, onUseImage, onEditMessage, onSwitchVersion, versionsDisabled,
}) => {
  const t = useT();
  const [elapsed, setElapsed] = useState(0);
  const [draft, setDraft] = useState(null); // text while this message is being edited
  const [copied, copy] = useCopy();
  const [showGame, setShowGame] = useState(() => readStored(GAME_VISIBLE_KEY) !== 'hidden');
  const isProcessing = message.status === 'processing';
  const draftRef = useRef(null);

  // Elapsed-seconds counter while generating, so a 30-60s wait doesn't look stuck.
  useEffect(() => {
    if (!isProcessing) return undefined;
    const startedAt = message.createdAt ? new Date(message.createdAt).getTime() : Date.now();
    const tick = () => setElapsed(Math.max(0, Math.round((Date.now() - startedAt) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [isProcessing, message.createdAt]);

  // Editing: the box grows with the text, the cursor starts at the end.
  useEffect(() => {
    const el = draftRef.current;
    if (draft === null || !el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [draft]);
  const startEditing = () => {
    setDraft(message.content || '');
    setTimeout(() => {
      const el = draftRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    }, 0);
  };
  const submitEdit = () => {
    const text = (draft || '').trim();
    if (!text && !message.attachments?.length) return;
    setDraft(null);
    onEditMessage(text);
  };

  if (message.role === 'user') {
    const versions = message.versions?.total > 1 ? message.versions : null;
    return (
      <div className="group/user flex flex-col items-end gap-1.5 px-4 py-2">
        {message.attachments?.length > 0 && (
          <div className="flex flex-wrap justify-end gap-2 max-w-[85%] sm:max-w-[75%]">
            {message.attachments.map((a) => (
              <SentAttachment key={a.filename} attachment={a} onOpen={onOpen} onEdit={onEdit} />
            ))}
          </div>
        )}
        {draft !== null ? (
          <div className="w-full max-w-[85%] sm:max-w-[75%] rounded-2xl bg-gray-100 p-3">
            <textarea
              ref={draftRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitEdit(); }
                if (e.key === 'Escape') setDraft(null);
              }}
              aria-label={t('mediaAI.editMessage')}
              rows={2}
              className="w-full resize-none bg-transparent text-[15px] leading-relaxed text-gray-800 focus:outline-none"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="px-3 py-1.5 rounded-full text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
              >
                {t('mediaAI.cancel')}
              </button>
              <button
                type="button"
                onClick={submitEdit}
                disabled={!draft.trim() && !message.attachments?.length}
                className="px-3 py-1.5 rounded-full text-sm font-medium text-white bg-gray-900 hover:bg-black disabled:opacity-40"
              >
                {t('mediaAI.send')}
              </button>
            </div>
          </div>
        ) : (
          message.content && (
            <div className="max-w-[85%] sm:max-w-[75%] bg-primary-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap break-words">
              {message.content}
            </div>
          )
        )}
        {draft === null && (message.content || onEditMessage || versions) && (
          <div className="flex items-center gap-1">
            <div className="flex items-center opacity-0 group-hover/user:opacity-100 focus-within:opacity-100 transition-opacity">
              {message.content && (
                <MessageAction title={copied ? t('mediaAI.copied') : t('mediaAI.copy')} onClick={() => copy(message.content)}>
                  {copied ? <FiCheck size={14} className="text-green-500" /> : <FiCopy size={14} />}
                </MessageAction>
              )}
              {onEditMessage && (
                <MessageAction title={t('mediaAI.editMessage')} onClick={startEditing}>
                  <FiEdit2 size={14} />
                </MessageAction>
              )}
            </div>
            {versions && <VersionSwitcher versions={versions} onSwitch={onSwitchVersion} disabled={versionsDisabled || !onSwitchVersion} />}
          </div>
        )}
      </div>
    );
  }

  const groups = groupBySection(message);
  const textOnly = !message.outputs?.length && message.status === 'completed' && message.content;

  return (
    <div className="group/reply px-4 py-2">
      <div className="max-w-[95%] sm:max-w-[85%] space-y-2">
        {isProcessing && (
          <div className="space-y-2">
            <div className="flex items-center gap-3 w-full max-w-md rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-500 border-t-transparent flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-700">{message.progress || t('mediaAI.creating')}</p>
                <p className="text-xs text-gray-400">{t('mediaAI.creatingHint')} · {elapsed}s</p>
              </div>
              <button
                type="button"
                onClick={() => { const next = !showGame; setShowGame(next); writeStored(GAME_VISIBLE_KEY, next ? 'shown' : 'hidden'); }}
                className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                <FiPlay size={11} />
                {showGame ? t('mediaAI.games.hide') : t('mediaAI.games.show')}
              </button>
            </div>
            {showGame && <LoaderGames />}
          </div>
        )}

        {message.status === 'failed' && (
          <div className="flex items-start gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5 max-w-md">
            <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span className="flex-1">{message.error || t('mediaAI.failed')}</span>
            {onRetry && (
              <button
                onClick={onRetry}
                className="flex-shrink-0 inline-flex items-center gap-1 font-medium text-danger-700 hover:underline"
              >
                <FiRefreshCw size={12} />
                {t('mediaAI.tryAgain')}
              </button>
            )}
          </div>
        )}

        {message.status === 'stopped' && (
          <div className="flex items-center gap-2 text-sm text-gray-500 italic">
            <FiSquare size={11} className="flex-shrink-0" />
            <span>{t('mediaAI.stopped')}</span>
            {onRetry && (
              <button onClick={onRetry} className="not-italic inline-flex items-center gap-1 font-medium text-primary-600 hover:underline">
                <FiRefreshCw size={12} />
                {t('mediaAI.tryAgain')}
              </button>
            )}
          </div>
        )}

        {message.content && (
          <div className="prose prose-sm max-w-none text-[15px] leading-relaxed text-gray-800 break-words [&_p]:my-1.5 [&_ol]:my-2 [&_ul]:my-2 [&_li]:my-0.5">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>{message.content}</ReactMarkdown>
          </div>
        )}
        {textOnly && (
          <div className="opacity-0 group-hover/reply:opacity-100 focus-within:opacity-100 transition-opacity">
            <MessageAction title={copied ? t('mediaAI.copied') : t('mediaAI.copy')} onClick={() => copy(message.content)}>
              {copied ? <FiCheck size={14} className="text-green-500" /> : <FiCopy size={14} />}
            </MessageAction>
          </div>
        )}

        {groups.length > 0 ? (
          <div className="space-y-5 pt-1">
            {groups.map((group, i) => (
              <section key={group.title + i}>
                <h3 className="text-[15px] font-semibold text-gray-900 mb-2">{i + 1}. {group.title}</h3>
                <div className="grid gap-2.5 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                  {group.outputs.map((output) => (
                    <OutputMedia key={output.filename} output={output} onOpen={onOpen} onEdit={onEdit} onUseImage={onUseImage} compact />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : message.outputs?.length > 0 && (
          <div className={`grid gap-3 ${outputGridClass(message.outputs)}`}>
            {message.outputs.map((output) => (
              <OutputMedia key={output.filename} output={output} onOpen={onOpen} onEdit={onEdit} onUseImage={onUseImage} />
            ))}
          </div>
        )}

        {message.outputs?.some((o) => o.source) && (
          <p className="text-[11px] text-amber-700">{t('mediaAI.fromWeb')}</p>
        )}

        {message.outputs?.length > 0 && PROVIDER_NAMES[message.provider] && (
          <p className="text-[11px] text-gray-400">{t('mediaAI.madeWith', { provider: PROVIDER_NAMES[message.provider] })}</p>
        )}
      </div>
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

export const MediaAI = () => {
  const t = useT();
  const { user } = useAuthStore();
  const hasAccess = hasFeature(user, 'mediaAI');
  const canViewTeamHistory = ADMIN_HISTORY_ROLES.includes(user?.role);

  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]); // [{ id, file, previewUrl }]
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [dragging, setDragging] = useState(false);
  // Providers that can make images right now. The picker only appears when
  // there is more than one; video has a single provider, so no choice there.
  const [imageProviders, setImageProviders] = useState([]);
  const [imageProvider, setImageProvider] = useState('');
  // Video models that are set up ({ id, name, providerName, price: { '720p', '1080p' } }) and the chosen one.
  const [videoModels, setVideoModels] = useState([]);
  const [videoModel, setVideoModel] = useState('');
  const [aspectRatio, setAspectRatio] = useState(() => {
    const stored = readStored(ASPECT_STORAGE_KEY);
    return ASPECT_RATIOS.includes(stored) ? stored : DEFAULT_ASPECT;
  });
  // Pictures already in this chat that the user picked to work from next.
  const [pendingRefs, setPendingRefs] = useState([]); // [{ filename, url }]
  const [editing, setEditing] = useState(null); // { image, url } while the editor is open

  const [sidebarTab, setSidebarTab] = useState('mine'); // 'mine' | 'team'
  const [teamThreads, setTeamThreads] = useState([]);
  const [teamThreadsLoading, setTeamThreadsLoading] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [viewingOwner, setViewingOwner] = useState(null);

  // The user's own saved prompts. Picking one pulls its text into the message
  // box, where it can be edited for this request or added to.
  const { prompts, limits: promptLimits, loaded: promptsLoaded, reload: reloadPrompts } = useCustomPrompts('mediaAI');
  const [activePromptId, setActivePromptId] = useState(null);
  const [promptManager, setPromptManager] = useState(null); // null | { initialText }

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeThreadIdRef = useRef(null); // guards the poll loop against a thread switch
  const defaultAppliedRef = useRef(false);

  const isGenerating = messages.some((m) => m.status === 'processing');
  const isBusy = isSending || isGenerating;

  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  const refreshThreads = useCallback(async () => {
    try {
      setThreads((await mediaAIService.listThreads()) || []);
    } catch {
      // Silent — the chat itself still works without a thread list
    } finally {
      setThreadsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAccess) refreshThreads();
  }, [hasAccess, refreshThreads]);

  useEffect(() => {
    if (!hasAccess) return;
    mediaAIService.getProviders()
      .then((providers) => {
        const available = providers?.image || [];
        setImageProviders(available);
        const models = providers?.videoModels || [];
        setVideoModels(models);
        const rememberedModel = readStored(VIDEO_MODEL_STORAGE_KEY);
        setVideoModel(models.some((m) => m.id === rememberedModel) ? rememberedModel : (providers?.defaultVideoModel || ''));
        const remembered = localStorage.getItem(IMAGE_PROVIDER_STORAGE_KEY);
        setImageProvider(available.some((p) => p.id === remembered) ? remembered : (available[0]?.id || ''));
      })
      .catch(() => { /* picker stays hidden; the server falls back to its default provider */ });
  }, [hasAccess]);

  const chooseImageProvider = (id) => {
    setImageProvider(id);
    localStorage.setItem(IMAGE_PROVIDER_STORAGE_KEY, id);
  };

  const chooseVideoModel = (id) => {
    setVideoModel(id);
    writeStored(VIDEO_MODEL_STORAGE_KEY, id);
  };

  const chooseAspectRatio = (ratio) => {
    setAspectRatio(ratio);
    writeStored(ASPECT_STORAGE_KEY, ratio);
  };

  const handleUseImage = (output, url) => {
    setPendingRefs((prev) => (prev.some((r) => r.filename === output.filename) ? prev : [...prev, { filename: output.filename, url }]));
    toast.info(t('mediaAI.referenceAdded'), { autoClose: 2000 });
    textareaRef.current?.focus();
  };

  // ── Stop, edit an earlier message, switch versions (as in ChatGPT) ────────
  const handleStop = async () => {
    const threadId = activeThreadIdRef.current;
    if (!threadId) return;
    try {
      const thread = await mediaAIService.stopGeneration(threadId);
      if (activeThreadIdRef.current === threadId) setMessages(thread.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.message || t('mediaAI.stopFailed'));
    }
  };

  const handleEditMessage = async (messageId, text) => {
    const threadId = activeThreadIdRef.current;
    setError('');
    jumpToLatest();
    try {
      const thread = await mediaAIService.editMessage({
        threadId,
        messageId,
        message: text,
        imageProvider: imageProviders.length > 1 ? imageProvider : undefined,
        aspectRatio,
        videoModel: videoModel || undefined,
      });
      if (activeThreadIdRef.current === threadId) setMessages(thread.messages || []);
      refreshThreads();
    } catch (e) {
      toast.error(e.response?.data?.message || t('mediaAI.failed'));
    }
  };

  const handleSwitchVersion = async (group, order) => {
    const threadId = activeThreadIdRef.current;
    try {
      const thread = await mediaAIService.switchVersion({ threadId, group, order });
      if (activeThreadIdRef.current === threadId) setMessages(thread.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.message || t('mediaAI.loadFailed'));
    }
  };

  // Errors are left to the editor, which shows them and stays open.
  const handleApplyEdit = async ({ tool, params, mask }) => {
    const thread = await mediaAIService.editImage({
      threadId: activeThreadIdRef.current,
      filename: editing.image.filename,
      tool,
      params,
      mask,
    });
    setMessages(thread.messages || []);
    setEditing(null);
    refreshThreads();
  };

  const refreshTeamThreads = useCallback(async (userName) => {
    setTeamThreadsLoading(true);
    try {
      const result = await mediaAIService.listThreadsForAdmin({ userName: userName || undefined });
      setTeamThreads(result?.data || []);
    } catch {
      toast.error('Could not load team history.');
    } finally {
      setTeamThreadsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canViewTeamHistory || sidebarTab !== 'team') return undefined;
    const timer = setTimeout(() => refreshTeamThreads(teamSearch), teamSearch ? 300 : 0);
    return () => clearTimeout(timer);
  }, [canViewTeamHistory, sidebarTab, teamSearch, refreshTeamThreads]);

  // ── Poll the open chat while its last reply is still being generated ──────
  useEffect(() => {
    if (!isGenerating || !activeThreadId || viewingOwner) return undefined;
    const threadId = activeThreadId;
    const timer = setInterval(async () => {
      try {
        const thread = await mediaAIService.getThread(threadId);
        if (activeThreadIdRef.current !== threadId) return;
        setMessages(thread.messages || []);
        if (!(thread.messages || []).some((m) => m.status === 'processing')) refreshThreads();
      } catch {
        // transient — the next tick retries
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isGenerating, activeThreadId, viewingOwner, refreshThreads]);

  // Follow new content only while the user is at the bottom of the chat. The
  // poll replaces `messages` every few seconds while a request runs, which used
  // to yank anyone reading older results back down each time.
  const followLatestRef = useRef(true);
  const handleMessagesScroll = (e) => {
    const el = e.currentTarget;
    followLatestRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  const jumpToLatest = () => { followLatestRef.current = true; };

  useEffect(() => {
    if (followLatestRef.current) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  // Release object URLs for attachment previews when the page unmounts.
  const pendingFilesRef = useRef([]);
  pendingFilesRef.current = pendingFiles;
  useEffect(() => () => pendingFilesRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl)), []);

  // ── Attachments ───────────────────────────────────────────────────────────
  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const accepted = [];
    for (const file of incoming) {
      if (!isAllowedFile(file)) {
        toast.error(t('mediaAI.unsupportedFile', { name: file.name }));
      } else if (file.size > MAX_FILE_BYTES) {
        toast.error(t('mediaAI.fileTooLarge', { name: file.name }));
      } else {
        accepted.push({ id: `${file.name}-${file.size}-${Math.random()}`, file, previewUrl: URL.createObjectURL(file) });
      }
    }
    setPendingFiles((prev) => {
      const next = [...prev, ...accepted];
      if (next.length > MAX_FILES) {
        toast.error(t('mediaAI.tooManyFiles', { max: MAX_FILES }));
        next.slice(MAX_FILES).forEach((p) => URL.revokeObjectURL(p.previewUrl));
      }
      return next.slice(0, MAX_FILES);
    });
  };

  const removePendingFile = (id) => {
    setPendingFiles((prev) => {
      prev.filter((p) => p.id === id).forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return prev.filter((p) => p.id !== id);
    });
  };

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!viewingOwner) addFiles(e.dataTransfer?.files);
  };

  // ── Saved prompts ─────────────────────────────────────────────────────────
  // Put the chosen prompt's text at the top of the message box. Anything the
  // user typed below a previously chosen prompt is kept; that prompt is swapped
  // out ("No prompt" just removes it).
  const choosePrompt = (id) => {
    const previous = prompts.find((p) => p.id === activePromptId)?.text;
    const next = prompts.find((p) => p.id === id);
    setInput((current) => {
      const rest = (previous && current.startsWith(previous) ? current.slice(previous.length) : current).replace(/^\s+/, '');
      if (!next) return rest;
      return rest ? `${next.text}\n\n${rest}` : next.text;
    });
    setActivePromptId(next ? next.id : null);
    textareaRef.current?.focus();
  };

  const applyDefaultPrompt = useCallback(() => {
    const preferred = prompts.find((p) => p.isDefault);
    setInput(preferred ? preferred.text : '');
    setActivePromptId(preferred ? preferred.id : null);
  }, [prompts]);

  // Start with the default prompt in an empty composer, once prompts have loaded.
  useEffect(() => {
    if (!promptsLoaded || defaultAppliedRef.current) return;
    defaultAppliedRef.current = true;
    if (!input.trim()) applyDefaultPrompt();
  }, [promptsLoaded, applyDefaultPrompt, input]);

  // ── Threads ───────────────────────────────────────────────────────────────
  const handleNewChat = () => {
    jumpToLatest();
    setSidebarTab('mine');
    setViewingOwner(null);
    setActiveThreadId(null);
    setMessages([]);
    setPendingRefs([]);
    setError('');
    if (!input.trim()) applyDefaultPrompt();
    textareaRef.current?.focus();
  };

  const handleSelectThread = async (id) => {
    jumpToLatest();
    if (id === activeThreadId && !viewingOwner) return;
    setMobileSidebarOpen(false);
    try {
      const thread = await mediaAIService.getThread(id);
      setMessages(thread.messages || []);
      setActiveThreadId(id);
      setViewingOwner(null);
      setPendingRefs([]);
      setError('');
    } catch {
      toast.error(t('mediaAI.loadFailed'));
    }
  };

  const handleSelectTeamThread = async (id) => {
    jumpToLatest();
    if (id === activeThreadId) return;
    setMobileSidebarOpen(false);
    try {
      const thread = await mediaAIService.getThreadForAdmin(id);
      setMessages(thread.messages || []);
      setActiveThreadId(id);
      setViewingOwner(thread.userId ? { name: thread.userId.name, email: thread.userId.email } : null);
      setError('');
    } catch {
      toast.error(t('mediaAI.loadFailed'));
    }
  };

  const handleDeleteThread = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t('mediaAI.deleteConfirm'))) return;
    try {
      await mediaAIService.deleteThread(id);
      setThreads((prev) => prev.filter((th) => th._id !== id));
      if (id === activeThreadId) {
        setActiveThreadId(null);
        setMessages([]);
        setPendingRefs([]);
      }
    } catch {
      toast.error(t('mediaAI.deleteFailed'));
    }
  };

  // ── Send ──────────────────────────────────────────────────────────────────
  // retryText: resend an earlier request as-is (its attachments are already in the chat).
  const handleSend = async (retryText) => {
    const isRetry = typeof retryText === 'string';
    const text = isRetry ? retryText : input.trim();
    const filesToSend = isRetry ? [] : pendingFiles;
    const refsToSend = isRetry ? [] : pendingRefs;
    if ((!text && !filesToSend.length && !refsToSend.length) || isBusy || viewingOwner) return;

    setError('');
    setIsSending(true);
    jumpToLatest(); // the user's own new request — show it
    try {
      const thread = await mediaAIService.sendMessage({
        threadId: activeThreadIdRef.current,
        message: text,
        files: filesToSend.map((p) => p.file),
        imageProvider: imageProviders.length > 1 ? imageProvider : undefined,
        aspectRatio,
        referenceFiles: refsToSend.map((r) => r.filename),
        videoModel: videoModel || undefined,
      });
      if (!isRetry) {
        pendingFiles.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        setPendingFiles([]);
        setPendingRefs([]);
        setInput('');
        setActivePromptId(null);
      }
      setActiveThreadId(thread._id);
      activeThreadIdRef.current = thread._id;
      setMessages(thread.messages || []);
      refreshThreads();
    } catch (e) {
      // Input and attachments are kept so the user can fix and resend.
      setError(e.response?.data?.message || e.message || t('mediaAI.failed'));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatThreadDate = (iso) => {
    const d = new Date(iso);
    const isToday = d.toDateString() === new Date().toDateString();
    return isToday
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center text-center h-64 max-w-md mx-auto gap-3">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
          <FiLock size={20} className="text-gray-400" />
        </div>
        <h1 className="text-lg font-semibold text-gray-900">{t('mediaAI.noAccessTitle')}</h1>
        <p className="text-sm text-gray-500">{t('mediaAI.noAccessDesc')}</p>
      </div>
    );
  }

  // ── Thread list (shared between desktop rail + mobile drawer) ─────────────
  const threadList = (
    <>
      <button
        onClick={handleNewChat}
        className="flex items-center gap-2 mx-2 mt-2 mb-1 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors"
      >
        <FiPlus size={15} />
        {t('mediaAI.newChat')}
      </button>

      {canViewTeamHistory && (
        <div className="flex mx-2 mt-1 mb-1 p-0.5 rounded-lg bg-gray-100 text-xs font-medium">
          <button
            onClick={() => setSidebarTab('mine')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md transition-colors ${
              sidebarTab === 'mine' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiUser size={12} />
            {t('mediaAI.myChats')}
          </button>
          <button
            onClick={() => setSidebarTab('team')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md transition-colors ${
              sidebarTab === 'team' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiUsers size={12} />
            {t('mediaAI.team')}
          </button>
        </div>
      )}

      {sidebarTab === 'team' && canViewTeamHistory ? (
        <>
          <div className="relative mx-2 mb-1">
            <FiSearch size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              placeholder={t('mediaAI.searchUser')}
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
            {teamThreadsLoading ? (
              <p className="px-3 py-2 text-xs text-gray-400">{t('mediaAI.loadingChats')}</p>
            ) : teamThreads.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-400 text-center">{t('mediaAI.noTeamChats')}</p>
            ) : (
              teamThreads.map((th) => (
                <button
                  key={th._id}
                  onClick={() => handleSelectTeamThread(th._id)}
                  className={`w-full flex flex-col gap-0.5 px-2.5 py-2 rounded-lg text-left text-sm transition-colors ${
                    th._id === activeThreadId ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <FiMessageSquare size={13} className="flex-shrink-0 text-gray-400" />
                    <span className="flex-1 min-w-0 truncate">{th.title || t('mediaAI.newChat')}</span>
                    <span className="flex-shrink-0 text-[10px] text-gray-400">{formatThreadDate(th.updatedAt)}</span>
                  </span>
                  <span className="pl-5 text-[11px] text-gray-400 truncate">{th.userId?.name || th.userId?.email || ''}</span>
                </button>
              ))
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
          {threadsLoading ? (
            <p className="px-3 py-2 text-xs text-gray-400">{t('mediaAI.loadingChats')}</p>
          ) : threads.length === 0 ? (
            <p className="px-3 py-4 text-xs text-gray-400 text-center">{t('mediaAI.noChats')}</p>
          ) : (
            threads.map((th) => (
              <button
                key={th._id}
                onClick={() => handleSelectThread(th._id)}
                className={`group/thread w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-sm transition-colors ${
                  th._id === activeThreadId && !viewingOwner
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <FiMessageSquare size={13} className="flex-shrink-0 text-gray-400" />
                <span className="flex-1 min-w-0 truncate">{th.title || t('mediaAI.newChat')}</span>
                <span className="flex-shrink-0 text-[10px] text-gray-400 group-hover/thread:hidden">
                  {formatThreadDate(th.updatedAt)}
                </span>
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => handleDeleteThread(th._id, e)}
                  className="hidden group-hover/thread:flex items-center justify-center w-5 h-5 rounded text-gray-400 hover:text-danger-600 hover:bg-danger-50 flex-shrink-0"
                  title={t('mediaAI.deleteChat')}
                >
                  <FiTrash2 size={12} />
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-8.5rem)] min-h-[480px] -mx-3 sm:-mx-4 md:-mx-6 -mb-3 sm:-mb-4 md:-mb-6">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 sm:px-6 pb-3 flex-shrink-0">
        <button
          onClick={() => setMobileSidebarOpen((v) => !v)}
          className="md:hidden text-gray-500 hover:text-gray-700"
          aria-label="Toggle chat list"
        >
          <FiMenu size={18} />
        </button>
        <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
          <FiImage size={17} className="text-primary-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">{t('mediaAI.title')}</h1>
          <p className="text-xs text-gray-500 leading-tight hidden sm:block">{t('mediaAI.desc')}</p>
        </div>
      </div>

      {/* Body: thread rail + chat panel */}
      <div className="flex-1 flex border-t border-gray-200 overflow-hidden bg-white">
        <div className="hidden md:flex md:flex-col w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50/50">
          {threadList}
        </div>

        {mobileSidebarOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileSidebarOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col shadow-xl">
              <div className="flex items-center justify-between px-3 py-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-700">{t('mediaAI.chats')}</span>
                <button onClick={() => setMobileSidebarOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <FiX size={18} />
                </button>
              </div>
              {threadList}
            </div>
          </div>
        )}

        {/* Chat column — also the drop target for attachments */}
        <div
          className="relative flex-1 flex flex-col min-w-0"
          onDragOver={(e) => { e.preventDefault(); if (!viewingOwner) setDragging(true); }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
          onDrop={handleDrop}
        >
          {dragging && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary-50/90 border-2 border-dashed border-primary-400 pointer-events-none">
              <p className="text-sm font-medium text-primary-700">{t('mediaAI.dropHere')}</p>
            </div>
          )}

          {viewingOwner && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex-shrink-0">
              <FiEye size={13} className="flex-shrink-0" />
              <span>
                {t('mediaAI.viewingAs')} <strong>{viewingOwner.name || viewingOwner.email}</strong> — {t('mediaAI.readOnly')}
              </span>
            </div>
          )}

          <div className="flex-1 overflow-y-auto py-3" onScroll={handleMessagesScroll}>
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-2">
                <div className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center mb-1">
                  <FiImage size={22} className="text-primary-600" />
                </div>
                <p className="text-gray-700 font-semibold">{t('mediaAI.emptyTitle')}</p>
                <p className="text-sm text-gray-400 max-w-sm">{t('mediaAI.emptyDesc')}</p>
              </div>
            ) : (
              <>
                {messages.map((m, idx) => {
                  // Retry is offered on the latest reply only, when it failed or was stopped and the request before it had text.
                  const request = messages[idx - 1];
                  const canRetry = !viewingOwner && !isBusy && idx === messages.length - 1
                    && (m.status === 'failed' || m.status === 'stopped') && request?.role === 'user' && request.content;
                  // Any of the user's own messages can be edited and resent — even while
                  // a reply is being made (that reply is stopped first).
                  const canEditMessage = !viewingOwner && !isSending && m.role === 'user';
                  return (
                    <MessageBubble
                      key={m._id}
                      message={m}
                      onOpen={setLightboxUrl}
                      onRetry={canRetry ? () => handleSend(request.content) : undefined}
                      onEdit={!viewingOwner && !isBusy ? (image, url) => setEditing({ image, url }) : undefined}
                      onUseImage={!viewingOwner ? handleUseImage : undefined}
                      onEditMessage={canEditMessage ? (text) => handleEditMessage(m._id, text) : undefined}
                      onSwitchVersion={!viewingOwner ? handleSwitchVersion : undefined}
                      versionsDisabled={isBusy}
                    />
                  );
                })}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {error && (
            <div className="mx-4 mb-2 flex items-start gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5 flex-shrink-0">
              <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => setError('')} className="text-danger-400 hover:text-danger-600"><FiX size={12} /></button>
            </div>
          )}

          {/* Composer — hidden while viewing someone else's read-only chat */}
          {!viewingOwner && (
            <div className="flex-shrink-0 border-t border-gray-200 px-3 sm:px-4 py-3">
              <div className="max-w-3xl mx-auto">
                {pendingRefs.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {pendingRefs.map((r) => (
                      <div key={r.filename} className="relative w-16 h-16 rounded-lg overflow-hidden border-2 border-primary-300 bg-gray-100">
                        <img src={r.url} alt="" className="w-full h-full object-cover" />
                        <span className="absolute bottom-0.5 left-0.5 flex items-center justify-center w-4 h-4 rounded bg-black/60 text-white">
                          <FiLink size={9} />
                        </span>
                        <button
                          onClick={() => setPendingRefs((prev) => prev.filter((p) => p.filename !== r.filename))}
                          title={t('mediaAI.removeFile')}
                          className="absolute top-0.5 right-0.5 flex items-center justify-center w-4 h-4 rounded-full bg-black/60 text-white hover:bg-black/80"
                        >
                          <FiX size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {pendingFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {pendingFiles.map((p) => (
                      <div key={p.id} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-100" title={p.file.name}>
                        {p.file.type.startsWith('video/') ? (
                          <>
                            <video src={p.previewUrl} muted preload="metadata" className="w-full h-full object-cover" />
                            <span className="absolute bottom-0.5 left-0.5 flex items-center justify-center w-4 h-4 rounded bg-black/60 text-white">
                              <FiFilm size={10} />
                            </span>
                          </>
                        ) : (
                          <img src={p.previewUrl} alt={p.file.name} className="w-full h-full object-cover" />
                        )}
                        <button
                          onClick={() => removePendingFile(p.id)}
                          title={t('mediaAI.removeFile')}
                          className="absolute top-0.5 right-0.5 flex items-center justify-center w-4 h-4 rounded-full bg-black/60 text-white hover:bg-black/80"
                        >
                          <FiX size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-2 text-xs text-gray-500">
                  {/* Saved prompts: picking one puts its text in the message box to use or refine */}
                  <div className="flex items-center gap-2 min-w-0">
                    <label htmlFor="media-ai-prompt">{t('prompts.label')}</label>
                    <PromptPicker
                      id="media-ai-prompt"
                      prompts={prompts}
                      value={activePromptId}
                      onChange={choosePrompt}
                      disabled={isBusy}
                      allowNone
                      className="w-[180px] sm:w-[220px]"
                    />
                    <button
                      type="button"
                      onClick={() => setPromptManager({ initialText: '' })}
                      className="inline-flex items-center gap-1 font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap"
                    >
                      <FiSettings size={11} />
                      {t('prompts.manageButton')}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span id="aspect-ratio-label">{t('mediaAI.shape')}</span>
                    <div role="radiogroup" aria-labelledby="aspect-ratio-label" className="flex p-0.5 rounded-lg bg-gray-100 font-medium">
                      {ASPECT_RATIOS.map((ratio) => (
                        <button
                          key={ratio}
                          type="button"
                          role="radio"
                          aria-checked={aspectRatio === ratio}
                          onClick={() => chooseAspectRatio(ratio)}
                          className={`px-2 py-1 rounded-md tabular-nums transition-colors ${
                            aspectRatio === ratio ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {ratio}
                        </button>
                      ))}
                    </div>
                  </div>
                  {imageProviders.length > 1 && (
                    <div className="flex items-center gap-2">
                      <span id="image-provider-label">{t('mediaAI.imagesBy')}</span>
                      <div role="radiogroup" aria-labelledby="image-provider-label" className="flex p-0.5 rounded-lg bg-gray-100 font-medium">
                        {imageProviders.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            role="radio"
                            aria-checked={imageProvider === p.id}
                            onClick={() => chooseImageProvider(p.id)}
                            className={`px-2.5 py-1 rounded-md transition-colors ${
                              imageProvider === p.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Video model — used when the request turns out to be a video. Price shown per second at 720p. */}
                  {videoModels.length > 0 && (
                    <div className="flex items-center gap-2 min-w-0">
                      <label htmlFor="media-ai-video-model">{t('mediaAI.videoModel')}</label>
                      <select
                        id="media-ai-video-model"
                        value={videoModel}
                        onChange={(e) => chooseVideoModel(e.target.value)}
                        disabled={isBusy}
                        className="max-w-[260px] rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                      >
                        {videoModels.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.providerName} · {m.name} — ${m.price['720p'].toFixed(3).replace(/0$/, '')}/s
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif,video/*"
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    title={t('mediaAI.attach')}
                    className="flex-shrink-0 w-10 h-10 rounded-full border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700 flex items-center justify-center transition-colors"
                  >
                    <FiPaperclip size={16} />
                  </button>
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder={t('mediaAI.inputPlaceholder')}
                    rows={1}
                    className="flex-1 resize-none rounded-2xl border border-gray-300 px-4 py-2.5 text-[15px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent max-h-[200px]"
                  />
                  {isGenerating && !isSending ? (
                    // While a reply is being made the send button becomes Stop, as in ChatGPT.
                    <button
                      onClick={handleStop}
                      title={t('mediaAI.stop')}
                      aria-label={t('mediaAI.stop')}
                      className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-900 text-white flex items-center justify-center transition-colors"
                    >
                      <FiSquare size={14} />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSend()}
                      disabled={isBusy || (!input.trim() && !pendingFiles.length && !pendingRefs.length)}
                      title={t('mediaAI.send')}
                      className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
                    >
                      {isSending ? (
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      ) : (
                        <FiSend size={15} />
                      )}
                    </button>
                  )}
                </div>
                <p className="text-center text-[11px] text-gray-400 mt-2 hidden sm:block">{t('mediaAI.disclaimer')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {promptManager && (
        <PromptManager
          scope="mediaAI"
          title={t('prompts.managerTitle', { place: 'Media AI' })}
          prompts={prompts}
          limits={promptLimits}
          initialText={promptManager.initialText}
          onChanged={reloadPrompts}
          onClose={() => setPromptManager(null)}
        />
      )}

      {editing && (
        <ImageEditor
          image={editing.image}
          imageUrl={editing.url}
          onApply={handleApplyEdit}
          onClose={() => setEditing(null)}
        />
      )}

      {/* Full-size image viewer */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <img src={lightboxUrl} alt="" className="max-w-full max-h-full rounded-lg" />
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 flex items-center justify-center w-9 h-9 rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default MediaAI;
